// Real case study data — sourced from Richie Akpan's paper trail + GitHub.

export const projects = [
  {
    id: "OP_01",
    slug: "vulnerability-management-lab-tenable",
    title: "Vulnerability Management Lab with Tenable",
    client: "PERSONAL LAB // ra-sys-io/GitHub",
    year: "2025",
    duration: "Cloud-hosted, hands-on",
    role: "Solo — Setup → Scan → Introduce → Remediate → Verify",
    scope: [
      "Tenable Vulnerability Management (cloud)",
      "Microsoft Azure VMs",
      "DISA STIG v3r2",
      "Credentialed scanning",
    ],
    githubUrl: "https://github.com/ra-sys-io/Vulnerability-Management-Lab-with-Tenable",
    image:
      "https://images.pexels.com/photos/37730212/pexels-photo-37730212.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    summary:
      "Cloud-based vulnerability management lab using Tenable's cloud console against a Windows 10 target in Azure. Ran a four-scan cycle: baseline → intentional misconfiguration → remediation → verification, tracking real DISA STIG v3r2 findings the whole way.",
    redacted:
      "Tenable VM cloud → Azure Windows 10 VM → credentialed scan → DISA STIG v3r2 baseline → intentional misconfig → remediate → rescan → verify.",
    metrics: [
      { k: "4", v: "SCAN_CYCLES" },
      { k: "STIG_v3r2", v: "DISA_FRAMEWORK" },
      { k: "3", v: "STIGs_TRACKED" },
    ],
    briefing:
      "Practical, cloud-hosted lab to internalize the vulnerability management lifecycle — baseline scanning, intentional misconfiguration, remediation, and validation — using Tenable's cloud VM platform and Azure infrastructure.",
    approach: [
      "Cloud environment: provisioned Microsoft Azure VMs — one hosting the Tenable Scan Engine, another as the Windows 10 Scan Target. Interfaced with the Tenable Vulnerability Management cloud console.",
      "Scan configuration: authored a credentialed Tenable scan combining a basic vulnerability policy with DISA Windows 10 STIG v3r2 compliance checks.",
      "Baseline (Scan 1): established a documented vulnerability + compliance baseline. Focus STIGs — WN10-AU-000505 (Security Event Log size), WN10-SO-000025 (Rename Guest Account), WN10-SO-000010 (Disable Guest Account).",
      "Intentional vulnerability (Scan 2): installed an outdated Firefox v110 and enabled the Guest Account (forcing WN10-SO-000010 to fail). Rescanned and confirmed Tenable detected both changes.",
      "Remediation (Scans 3–4): uninstalled Firefox, modified the registry to increase Security Event Log size, disabled and renamed the Guest Account, and fully patched Windows. Two rescans confirmed the vulnerability trend dropping.",
    ],
    outcome: [
      "Four-scan workflow that clearly maps a vulnerability from baseline → introduction → partial mitigation → full remediation, with annotated screenshots at every stage in the repo.",
      "Hands-on operation of Tenable's cloud VM console, credentialed scanning, and DISA Windows 10 STIG v3r2 compliance checking.",
      "Reproducible, evidence-first process — the same shape of work I apply to real vulnerability management at LOG(N) Pacific and UF Health.",
    ],
    tooling: [
      "Tenable Vulnerability Management (cloud)",
      "Tenable Scan Engine",
      "Microsoft Azure",
      "Windows 10",
      "DISA Windows 10 STIG v3r2",
      "LogN Pacific Cyber Range (optional)",
    ],
    quote: {
      text: "The lab isn't the point — the workflow is. Scan, remediate, verify, evidence. Repeat until the baseline is boring.",
      by: "Notes // Richie Akpan",
    },
  },
  {
    id: "OP_02",
    slug: "wazuh-home-lab",
    title: "Wazuh Home Lab — SIEM/XDR",
    client: "PERSONAL LAB // ra-sys-io/GitHub",
    year: "2025",
    duration: "In progress",
    role: "Solo — Architect → Deploy → Instrument → Attack Sim (WIP)",
    scope: ["Wazuh SIEM/XDR", "4-VM architecture", "Docker", "OWASP Juice Shop"],
    githubUrl: "https://github.com/ra-sys-io/Wazuh-Home-Lab",
    image:
      "https://images.pexels.com/photos/1089438/pexels-photo-1089438.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    summary:
      "Four-machine home lab built around Wazuh. An Ubuntu-hosted SIEM/XDR watches a Docker-based Debian web server running OWASP Juice Shop. Windows 11 endpoint and Kali attacker are next on the roadmap.",
    redacted:
      "Wazuh Manager + Indexer + Dashboard → Debian web server (Docker + NGINX + Juice Shop) → Wazuh Agent instrumented → Kali + Windows endpoints incoming.",
    metrics: [
      { k: "4-VM", v: "ARCHITECTURE" },
      { k: "6", v: "MILESTONES_DONE" },
      { k: "WIP", v: "ROADMAP_ACTIVE" },
    ],
    briefing:
      "Purpose: build a hands-on SOC learning environment that covers the full detection lifecycle — log ingest → detect → triage → respond. Wazuh gives me an open-source, fully-inspectable SIEM/XDR stack to grow with.",
    approach: [
      "Architecture: designed a 4-machine model — Wazuh Server (SIEM), Debian web server (target), Windows 11 endpoint (planned), Kali Linux (attacker).",
      "SIEM stack: deployed Wazuh Manager + Indexer + Dashboard on Ubuntu Server. All three components healthy and reachable.",
      "Target build-out: stood up Debian web server, hardened, then layered Docker, an NGINX reverse proxy, and OWASP Juice Shop as a purposely-vulnerable web app for detection practice.",
      "Agent instrumentation: installed and configured the Wazuh Agent on the web server. Logs flow into the SIEM in real time.",
      "Next milestones (roadmap): Windows 11 + Sysmon, Kali attacker, Suricata NIDS, custom Wazuh rules, web + Windows attack simulations, threat hunting scenarios, MITRE ATT&CK mapping, and active response.",
    ],
    outcome: [
      "Fully-deployed Wazuh SIEM/XDR platform (Manager + Indexer + Dashboard) on Ubuntu.",
      "Debian web server operational with Docker + NGINX reverse proxy fronting OWASP Juice Shop.",
      "Wazuh Agent instrumented and reporting; SIEM ingesting live web-server telemetry.",
      "Architecture diagram and step-by-step deployment guides committed to the repo for reproducibility.",
    ],
    tooling: [
      "Wazuh Manager + Indexer + Dashboard",
      "Ubuntu Server",
      "Debian",
      "Docker",
      "NGINX",
      "OWASP Juice Shop",
      "(planned) Windows 11 + Sysmon",
      "(planned) Kali Linux",
      "(planned) Suricata",
    ],
    quote: {
      text: "SIEM isn't just a tool — it's a discipline. This lab is where the discipline gets its reps.",
      by: "Notes // Richie Akpan",
    },
  },
];

export const getProject = (slug) => projects.find((p) => p.slug === slug);
